/**
 * Gamification features as a video-ad subject. Configurator art only (per the spec): the brand's ACTIVE
 * features are the `/configurator` gamification catalogue (`configuratorConfig.gamificationItems`) joined
 * with the brand's purchases (`selectActiveBrandGamificationItems`), keeping only purchases whose tier is a
 * non-off (enabled) rung. The key visual is the feature's `art_bundle[0].logo`; the name is its art-bundle
 * text (else the catalogue name). See `.claude/specs/2026-09-23-video-ad-subjects.md`.
 */
import type { StoryboardPromotion } from '@/store/slices/creativeJobs';
import type { GamificationItemWithTiers } from '@/store/slices/configurator-config';

import { artBundleText, enabledLadder } from '@/nova/utils/catalog';

import type { CreativeItem } from './creative-api';
import type { VideoSubject } from './video-subjects';

/** A brand's active gamification feature, flattened for the picker + the subject adapters. */
export interface GamificationFeature {
  /** The catalogue item id (as a string) — the subject id. */
  id: string;
  /** Display name (art-bundle text, else the catalogue name). */
  name: string;
  /** The feature's logo (key visual); may be '' when the catalogue carries none. */
  image: string;
}

/** A brand purchase, structurally (from `selectActiveBrandGamificationItems`). */
interface BrandItem {
  item_id: number;
  tier_id: string | number;
}

/**
 * The brand's ACTIVE gamification features: each purchase whose catalogue item exists and whose tier is an
 * enabled (non-off) rung, de-duplicated by item id, in purchase order. This mirrors the Product page's
 * "enabled feature" test (`featureFromApiItem`), reduced to what the picker needs.
 */
export const activeGamificationFeatures = (
  catalog: readonly GamificationItemWithTiers[],
  purchases: readonly BrandItem[],
  lang: string,
): GamificationFeature[] => {
  const seen = new Set<string>();
  const out: GamificationFeature[] = [];
  for (const purchase of purchases) {
    const item = catalog.find((entry) => entry.id === purchase.item_id);
    if (!item) continue;
    const enabled = enabledLadder(item).some((tier) => tier.id === Number(purchase.tier_id));
    if (!enabled) continue;
    const id = String(purchase.item_id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: artBundleText(item.art_bundle?.[0], lang) ?? item.name,
      image: item.art_bundle?.[0]?.logo ?? '',
    });
  }
  return out;
};

/** Adapt a feature to a normalised {@link VideoSubject}. No on-site cta_href, so the tracking link falls
 *  back to the brand homepage (per the spec's destination rule). */
export const gamificationSubjectFromFeature = (feature: GamificationFeature): VideoSubject => ({
  subject_type: 'gamification',
  id: feature.id,
  image_url: feature.image,
  title: feature.name,
});

/** Adapt a feature to the composer's {@link CreativeItem} pool shape (the logo is the key visual). */
export const creativeItemFromFeature = (feature: GamificationFeature): CreativeItem => ({
  banner_id: feature.id,
  image_url: feature.image,
  headline: feature.name,
  internal_name: feature.name,
});

/** Adapt a feature to a {@link StoryboardPromotion} for pre-seeding a gamification ad's draft storyboard.
 *  Lock-step with {@link creativeItemFromFeature} (same id + image) so the composer matches on reopen. */
export const storyboardPromotionFromFeature = (feature: GamificationFeature): StoryboardPromotion => ({
  banner_id: feature.id,
  image_url: feature.image,
  headline: feature.name,
});

/** Whether the gamification catalogue is still loading: not loaded, and either a read is in flight or none has
 *  failed yet. A failed read counts as settled (no features), so nothing waits on it forever. */
export const featuresLoadingFrom = (s: { loaded: boolean; fetching: boolean; error: unknown }): boolean =>
  !s.loaded && (s.fetching || s.error === null || s.error === undefined || s.error === '');
